import React, { useEffect, useRef, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { RouteComponentProps } from "react-router";
import {
  IonAvatar,
  IonAlert,
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonChip,
  IonCol,
  IonGrid,
  IonIcon,
  IonInput,
  IonItem,
  IonItemDivider,
  IonList,
  IonLoading,
  IonNote,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonToggle,
} from "@ionic/react";
import Content from "../components/Content";
import Uploader from "../components/Uploader";
import { useArtists } from "../data/store/useArtists";
import { useProfile } from "../data/store/useProfile";
import { usePlayer } from "../data/store/usePlayer";
import { useRoles } from "../data/store/useRoles";
import { useTitle } from "../data/store/useTitle";
import { useRegions } from "../data/store/useRegions";
import { sanitize, useCssImage, usePageTitle } from "../util/sugar";
import get from "lodash.get";
import RegionSelector from "../components/RegionSelector";
import { closeCircle, closeOutline } from "ionicons/icons";
import { LoginService } from "../services/LoginService";
import { buildUrl, iconUrl, ENABLE_REGIONS } from "../config/env";

interface AccountProps extends RouteComponentProps {}

const Account: React.FC<AccountProps> = () => {
  usePageTitle("Account");
  const { changeProfile } = useProfile();
  const loop = usePlayer((x) => x.loop);
  const toggleLoop = usePlayer((x) => x.toggleLoop);
  const autoplayPref = usePlayer((x) => x.autoplay);
  const toggleAutoplay = usePlayer((x) => x.toggleAutoplay);
  const isAuthenticated = useProfile((x) => x.authenticated);
  const autoplayOn = autoplayPref !== null ? autoplayPref : !isAuthenticated;
  const [showPlaybackOptions, setShowPlaybackOptions] = useState(false);
  const { role } = useRoles();
  const { fetchMeta, shouldFetch } = useArtists();
  const authorId = useProfile((x) => x.authorId) || "";
  const status = useProfile((x) => x.status) || "";
  const artist = useArtists((x) => x.retrieve(authorId));
  const setTitle = useTitle((x) => x.setTitle);
  const regions = useRegions((state) => state.regions);

  const [showAliasModifier, setShowAliasModifier] = useState(false);
  const [showWallModifier, setShowWallModifier] = useState(false);
  const [showAvatarModifier, setShowAvatarModifier] = useState(false);
  const [showWebsiteModifier, setShowWebsiteModifier] = useState(false);
  const [showPrivacyModifier, setShowPrivacyModifier] = useState(false);
  const [showStatementModifier, setShowStatementModifier] = useState(false);
  const [showDiscordModifier, setShowDiscordModifier] = useState(false);
  const [showRegionModifier, setShowRegionModifier] = useState(false);
  const [freshDiscord, setDiscord] = useState("");
  const [freshPrivacy, setPrivacy] = useState("");
  const [freshStatement, setStatement] = useState("");
  const [freshWebsite, setWebsite] = useState("");
  const [freshAlias, setAlias] = useState("");
  const [freshAvatar, setAvatar] = useState("");
  const [freshWallpaper, setWallpaper] = useState("");
  const [freshRegion, setRegion] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDangerZone, setShowDangerZone] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const isShowDashboard = get(role, "authority", []).some(
    (item) => item === "super admin" || item === "admin" || item === "moderator"
  );

  useEffect(() => {
    setTitle("account");
  }, [authorId, setTitle]);

  // Read the field itself on Save: ionChange is debounced/blur-driven, so the
  // React state can still hold the previous text when Save is tapped, which
  // saved the old value while the screen showed the new one.
  const aliasInput = useRef<HTMLIonInputElement>(null);
  const statementInput = useRef<HTMLIonTextareaElement>(null);
  const websiteInput = useRef<HTMLIonTextareaElement>(null);
  const discordInput = useRef<HTMLIonTextareaElement>(null);
  // Every profile write tells the user when it's refused instead of failing silently.
  const changeProfileOrWarn = (field: string, value: string, label: string = field) =>
    changeProfile(field, value).catch((error: any) => {
      console.error(`saving ${field} failed`, error);
      alert(`Your ${label} couldn't be saved. Please reload the page and try again.${error?.message ? `\n\n(${error.message})` : ""}`);
    });
  const saveField = async (
    field: string,
    label: string,
    ref: React.RefObject<HTMLIonInputElement | HTMLIonTextareaElement>,
    fallback: string,
    setLocal: (v: string) => void,
  ) => {
    const el = await (ref.current?.getInputElement() as Promise<HTMLInputElement | HTMLTextAreaElement> | undefined)?.catch(() => undefined);
    const value = (el?.value ?? fallback ?? "").trim();
    if (!value) return;
    setLocal(value);
    await changeProfileOrWarn(field, value, label);
  };

  useEffect(() => {
    if (status === "unauthenticated") {
      window.location.href = buildUrl('/login');
    }
    if (authorId && !artist) {
      shouldFetch(authorId) && fetchMeta(authorId);
    } else if (authorId && artist) {
      const { statement, website, wall, avatar, alias, privacy, region } = {
        ...artist,
      };
      statement && setStatement(statement);
      website && setWebsite(website);
      alias && setAlias(alias);
      wall && setWallpaper(wall);
      avatar && setAvatar(avatar);
      // No privacy field = no public opt-in yet; the rules treat it as
      // members-only, so show it that way.
      setPrivacy(privacy === "public" ? "public" : "member");
      region && setRegion(region);
    }
  }, [artist, authorId, fetchMeta, shouldFetch, status]);

  const artistBg = artist && useCssImage(freshWallpaper);

  return (
    <Content id="main">
      <IonGrid
        style={{
          "--ion-background-color": "clear!important",
          height: "100%",
          background: artistBg && artistBg,
        }}
      >
        <IonLoading isOpen={status === "unknown"} />
        <IonRow>
          <IonCol sizeMd="8" sizeLg="6" offsetMd="2" offsetLg="3">
            <IonCard className="ion-padding-top ion-text-center">
              <IonCardHeader>
                {artist && (
                  <IonAvatar
                    style={{ width: 108, height: 108, margin: "auto" }}
                  >
                    <img
                      src={freshAvatar || iconUrl('apple-touch-icon.png')}
                      alt={freshAlias || 'Avatar'}
                    />
                  </IonAvatar>
                )}
              </IonCardHeader>
              <IonCardContent>
                <h2>{freshAlias}</h2>
                <p>{freshStatement}</p>
                <a href={sanitize(freshWebsite)}>{freshWebsite}</a>
                {authorId && (
                  <IonList inset>
                    <IonItem onClick={() => setShowAliasModifier((x) => !x)}>
                      <IonChip>
                        {showAliasModifier ? "Your Alias" : "Change Alias"}
                      </IonChip>
                    </IonItem>
                    {showAliasModifier && (
                      <IonItem>
                        <IonInput
                          ref={aliasInput}
                          value={freshAlias}
                          aria-label="Your alias"
                          onIonChange={(e) => {
                            setAlias(e.detail.value!);
                          }}
                        />
                        <IonButtons slot="end">
                          <IonButton
                            fill="outline"
                            onClick={() => {
                              saveField("alias", "name", aliasInput, freshAlias, setAlias);
                              setShowAliasModifier((x) => !x);
                            }}
                          >
                            Save
                          </IonButton>
                        </IonButtons>
                      </IonItem>
                    )}
                    <IonItem
                      onClick={() => setShowStatementModifier((x) => !x)}
                    >
                      <IonChip>
                        {showStatementModifier
                          ? "Your Statement"
                          : "Change Statement"}
                      </IonChip>
                    </IonItem>
                    {showStatementModifier && (
                      <IonItem>
                        <IonTextarea
                          ref={statementInput}
                          value={freshStatement}
                          aria-label="Your statement"
                          onIonChange={(e) => {
                            setStatement(e.detail.value!);
                          }}
                        />
                        <IonButtons slot="end">
                          <IonButton
                            fill="outline"
                            onClick={() => {
                              saveField("statement", "statement", statementInput, freshStatement, setStatement);
                              setShowStatementModifier((x) => !x);
                            }}
                          >
                            Save
                          </IonButton>
                        </IonButtons>
                      </IonItem>
                    )}
                    <IonItem onClick={() => setShowWebsiteModifier((x) => !x)}>
                      <IonChip>
                        {showWebsiteModifier
                          ? "Your Website"
                          : "Change Website"}
                      </IonChip>
                    </IonItem>
                    {showWebsiteModifier && (
                      <IonItem>
                        <IonTextarea
                          ref={websiteInput}
                          value={freshWebsite}
                          aria-label="Your website"
                          onIonChange={(e) => {
                            setWebsite(e.detail.value!);
                          }}
                        />
                        <IonButtons slot="end">
                          <IonButton
                            fill="outline"
                            onClick={() => {
                              saveField("website", "website", websiteInput, freshWebsite, setWebsite);
                              setShowWebsiteModifier((x) => !x);
                            }}
                          >
                            Save
                          </IonButton>
                        </IonButtons>
                      </IonItem>
                    )}
                    {ENABLE_REGIONS && (
                      <>
                        <IonItem onClick={() => setShowRegionModifier((x) => !x)}>
                          <IonChip>
                            {showRegionModifier ? "Your Region" : "Change Region"}
                          </IonChip>
                          <IonButtons slot="end"></IonButtons>
                        </IonItem>
                        {showRegionModifier && (
                          <IonItem>
                            <RegionSelector
                              onChange={(regionId) => {
                                changeProfileOrWarn("region", regionId);
                                setRegion(regionId);
                              }}
                              value={freshRegion}
                            />
                            <IonButtons slot="end">
                              <IonButton
                                fill="clear"
                                onClick={() => {
                                  setRegion("XXX");
                                  changeProfileOrWarn("region", "XXX");
                                }}
                                aria-label="Clear region"
                              >
                                <IonIcon icon={closeOutline} color="danger" aria-hidden="true" />
                              </IonButton>
                            </IonButtons>
                          </IonItem>
                        )}
                      </>
                    )}
                    {showDiscordModifier && (
                      <IonItem>
                        <IonTextarea
                          placeholder="Copy and paste here from discord"
                          aria-label="Your Discord handle"
                          ref={discordInput}
                          value={freshDiscord}
                          onIonChange={(e) => {
                            setDiscord(e.detail.value!);
                          }}
                        />
                        <IonButtons slot="end">
                          <IonButton
                            fill="outline"
                            onClick={() => {
                              saveField("discord", "Discord handle", discordInput, freshDiscord, setDiscord);
                              setShowDiscordModifier((x) => !x);
                            }}
                          >
                            Save
                          </IonButton>
                        </IonButtons>
                      </IonItem>
                    )}
                    <IonItem onClick={() => setShowAvatarModifier((x) => !x)}>
                      <IonChip>
                        {showAvatarModifier
                          ? "Changing Avatar"
                          : "Change Avatar"}
                      </IonChip>
                    </IonItem>
                    {showAvatarModifier && (
                      <IonItem>
                        <Uploader
                          message="select your avatar"
                          collection="avatar"
                          onComplete={(media) => {
                            changeProfileOrWarn("avatar", media.src);
                            setShowAvatarModifier((x) => !x);
                            setAvatar(media.src);
                            artist && fetchMeta(artist.id);
                          }}
                        />
                        <IonButtons slot="end">
                          <IonButton
                            fill="clear"
                            aria-label="Reset avatar to default"
                            onClick={() => {
                              changeProfileOrWarn(
                                "avatar",
                                iconUrl('apple-touch-icon.png')
                              );
                              setAvatar(iconUrl('apple-touch-icon.png'));
                              artist && fetchMeta(artist.id);
                            }}
                          >
                            <IonIcon icon={closeOutline} color="danger" aria-hidden="true" />
                          </IonButton>
                        </IonButtons>
                      </IonItem>
                    )}
                    <IonItem onClick={() => setShowWallModifier((x) => !x)}>
                      <IonChip>
                        {showWallModifier
                          ? "Changing Wallpaper"
                          : "Change Wallpaper"}
                      </IonChip>
                    </IonItem>
                    {showWallModifier && (
                      <>
                        <IonItem>
                          <Uploader
                            message="select your wallpaper"
                            collection="wall"
                            onComplete={(media) => {
                              changeProfileOrWarn("wall", media.src);
                              setShowWallModifier((x) => !x);
                              setWallpaper(media.src);
                              artist && fetchMeta(artist.id);
                            }}
                          />
                          <IonButtons slot="end">
                            <IonButton
                              fill="clear"
                              aria-label="Reset wallpaper to default"
                              onClick={() => {
                                changeProfileOrWarn("wall", "/assets/icon/void.png");
                                setWallpaper("/assets/img/void.png");
                                artist && fetchMeta(artist.id);
                              }}
                            >
                              <IonIcon icon={closeOutline} color="danger" aria-hidden="true" />
                            </IonButton>
                          </IonButtons>
                        </IonItem>
                        <IonNote>
                          any size should work, small images will be tiled
                        </IonNote>
                      </>
                    )}
                    <IonItem onClick={() => setShowPrivacyModifier((x) => !x)}>
                      <IonChip>
                        {showPrivacyModifier
                          ? "Your Privacy"
                          : "Change Privacy"}
                      </IonChip>
                    </IonItem>
                    {showPrivacyModifier && (
                      <IonItem>
                        <IonButtons slot="start">
                          <IonToggle
                            aria-label="Members-only profile"
                            checked={freshPrivacy === "member"}
                            onIonChange={(e) => {
                              setPrivacy(
                                e.detail.checked ? "member" : "public"
                              );
                              changeProfileOrWarn(
                                "privacy",
                                e.detail.checked ? "member" : "public"
                              );
                            }}
                          />
                        </IonButtons>
                        <IonButtons slot="end">
                          {freshPrivacy === "member"
                            ? "Members Only"
                            : "Public"}
                        </IonButtons>
                      </IonItem>
                    )}
                    <IonItem onClick={() => setShowPlaybackOptions((x) => !x)}>
                      <IonChip>Playback Options</IonChip>
                    </IonItem>
                    {showPlaybackOptions && (
                      <>
                        <IonItem>
                          <IonButtons slot="start">
                            <IonToggle
                              aria-label="Loop playback"
                              checked={loop}
                              onIonChange={() => toggleLoop()}
                            />
                          </IonButtons>
                          <IonButtons slot="end">
                            {loop ? "Loop: On" : "Loop: Off"}
                          </IonButtons>
                        </IonItem>
                        <IonItem>
                          <IonButtons slot="start">
                            <IonToggle
                              aria-label="Autoplay"
                              checked={autoplayOn}
                              onIonChange={() => toggleAutoplay()}
                            />
                          </IonButtons>
                          <IonButtons slot="end">
                            {autoplayOn ? "Autoplay: On" : "Autoplay: Off"}
                          </IonButtons>
                        </IonItem>
                      </>
                    )}
                    <IonItem detail={false} href="javascript:void(0)"   onClick={()=>{setShowDangerZone(x=>!x)}}>
                     <IonChip>
                      Other Settings
                      </IonChip>
                      </IonItem>
                    <IonItemDivider color='clear'/>
                    {showDangerZone && (
                        <IonItem detail={false}  href="javascript:void(0)"                           color="danger"
                        onClick={() => setShowDeleteConfirm(true)}
>
                            Delete Account
                        </IonItem>
                      )}

                    <IonItem routerLink="/transmit" routerDirection="forward">
                      Transmit
                    </IonItem>
                    <IonItem routerLink="/favorites" routerDirection="forward">
                      Favorites
                    </IonItem>
                    {isShowDashboard && (
                      <IonItem routerLink="/admin">Admin</IonItem>
                    )}
                    <IonItem
                      routerLink={"/artist/" + authorId}
                      routerDirection="forward"
                    >
                      Artist Page
                    </IonItem>
                    {/* <IonItem routerLink="/sessions" routerDirection="none">
                      Sessions
                    </IonItem> */}
                    <IonItem routerLink="/logout" routerDirection="none">
                      Logout
                    </IonItem>
                
                    <IonAlert
                      isOpen={showDeleteConfirm}
                      onDidDismiss={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmText("");
                      }}
                      header="Delete Account"
                      message="To delete your account, type 'delete account' below. This action cannot be undone and will permanently delete all your data including songs, comments, and profile information."
                      inputs={[
                        {
                          name: 'confirmText',
                          type: 'text',
                          placeholder: 'Type "delete account"'
                        }
                      ]}
                      buttons={[
                        {
                          text: "Cancel",
                          role: "cancel",
                          handler: () => setDeleteConfirmText("")
                        },
                        {
                          text: "Delete",
                          role: "destructive",
                          cssClass: "alert-button-danger",
                          handler: async (data) => {
                            const confirmText = data.confirmText?.toLowerCase() || '';
                            setDeleteConfirmText(confirmText);
                            if (confirmText !== "delete account") {
                              return false;
                            }
                            try {
                              setIsDeleting(true);
                              const deleteAccount = httpsCallable(
                                getFunctions(),
                                "deleteAccount"
                              );
                              await deleteAccount();
                              LoginService.logout().then(async () => {
                                window.location.href = "/";
                              });
                            } catch (error) {
                              LoginService.logout().then(async () => {
                                window.location.href = "/";
                              });
                              console.error("Error deleting account:", error);
                              setIsDeleting(false);
                            }
                          }
                        }
                      ]}
                    />
                    <IonLoading
                      isOpen={isDeleting}
                      message="Deleting account..."
                    />
                  </IonList>
                )}
              </IonCardContent>
            </IonCard>
          </IonCol>
        </IonRow>
      </IonGrid>
    </Content>
  );
};

export default Account;
